<template>
  <FullPageLayout>
    <template v-if="fetchState.pending">
      <div>loading...</div>
    </template>
    <template v-else-if="fetchState.error">
      <ErrorPageLayout :error="fetchState.error" />
    </template>

    <template v-else>
      <div style="max-width: 1100px" class="mx-auto mt-4 w-full">
        <div id="masthead-background" class="bg-white p-3.75 sm:mb-6 mb-4 font-bold text-center u-center">
          <AdController unit="masthead" />
        </div>
        <div class="relative">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between sm:mb-6 mb-4 mx-4">
            <div class="flex items-center">
              <OBreadcrumb :items="breadcrumbItems" />
              <WidgetEmojile v-if="isTestCategory" class="ml-4" />
            </div>
            <KesfetRedirectButton class="my-6" />
          </div>
          <CategorySubBar :category="categoryData" />
          <ArticleSlider
            v-if="popularCarousel.length"
            class="px-4 sm:px-0"
            :articles="popularCarousel"
            arrow-color="#8197af"
            :data-layer="{ name: `Kategori Liste - Slider - ${categoryData.name}` }"
          >
            <SwiperSlide v-for="(article, index) in popularCarousel" :key="article._id">
              <CarouselArticle :article="article" variant="category" :lazy="index !== 0" />
            </SwiperSlide>
          </ArticleSlider>
          <div class="flex space-x-4">
            <div class="space-y-4 sm:space-y-6 sm:w-[730px] sm:flex-shrink-0">
              <template v-for="(article, index) in articles">
                <CategoryArticle
                  :key="article._id"
                  :article="article"
                  :data-layer="{ name: `Kategori Liste - Genel Listeleme - ${categoryData.name}`, position: index + 1 }"
                  :show-badge="isSponsored"
                />
                <CategoryAds v-if="!isSponsored" :key="article._id + '_ad'" :index="index" />
                <div v-if="popular.length > 0 && index === 9 && isMobile" :key="index">
                  <div class="flex items-center flex-nowrap my-3 pl-4">
                    <OIcon icon="trend-arrow" svg-class="w-7 h-7 text-[#424242]" />
                    <h3 class="mx-2 font-medium leading-18 text-24 text-primary-100 whitespace-nowrap">
                      {{ title }}
                    </h3>
                    <div class="border border-[#E2E5ED] w-full"></div>
                  </div>
                  <div class="space-y-3 sm:space-y-4">
                    <CategoryPopularArticle
                      v-for="(item, position) in popular"
                      :key="item._id"
                      class="mt-4"
                      :article="item"
                      :data-layer="{ name: `Kategori Liste - Sidebar - ${categoryData.name}`, position: position + 1 }"
                    />
                  </div>
                </div>
              </template>
              <!--<OPagination class="text-center sm:text-right" :current-page="categoryPageNumber" :total-count="pagination.total_items" />-->
              <OPaginationBase class="text-center" :current-page="categoryPageNumber" :has-next-page="pagination.hasNextPage" />
              <AdController unit="afterPagination" />
            </div>
            <aside class="flex-grow hidden sm:block">
              <div v-if="!isSponsored" class="bg-white p-2.5 flex flex-col items-center text-xs mb-2.5">
                <span>{{ $t('main.ads') }}</span>
                <AdController unit="sidebarTop" />
              </div>
              <div v-if="popular.length > 0" class="pb-10">
                <div class="flex items-center flex-nowrap my-3 pl-4">
                  <OIcon icon="trend-arrow" svg-class="w-7 h-7 text-[#424242]" />
                  <h3 class="mx-2 font-medium leading-18 text-24 text-primary-100 whitespace-nowrap">
                    {{ title }}
                  </h3>
                  <div class="border border-[#E2E5ED] w-full"></div>
                </div>
                <div class="space-y-3 sm:space-y-4">
                  <CategoryPopularArticle
                    v-for="(item, position) in popular"
                    :key="item._id"
                    class="mt-3"
                    :article="item"
                    :data-layer="{ name: `Kategori Liste - Sidebar - ${categoryData.name}`, position: position + 1 }"
                  />
                </div>
              </div>
              <div v-if="!isSponsored" class="bg-white p-2.5 flex flex-col items-center text-xs mt-2.5">
                <span>{{ $t('main.ads') }}</span>
                <AdController unit="sidebarBottom" />
              </div>
            </aside>
          </div>
        </div>
      </div>
      <AdController unit="bodyBottom" />
    </template>
  </FullPageLayout>
</template>
<script>
import { defineComponent, useContext, useFetch, ref, ssrRef, computed, onMounted, onUnmounted } from '@nuxtjs/composition-api';
import FullPageLayout from '@/modules/common/components/layout/FullPageLayout.vue';
import ErrorPageLayout from '@/modules/common/components/layout/ErrorPageLayout.vue';
import CategoryArticle from '@/modules/card/templates/category/CategoryArticle';
import CategorySubBar from '@/modules/category/components/CategorySubBar';
import { OBreadcrumb } from '@/modules/common/components/ui';
import { useCategoryMeta } from '@/modules/category/utils/categoryMeta';
import CategoryAds from '@/modules/category/components/CategoryAds.vue';
import AdController from '@/modules/ads/components/AdController';
import { initializeAdLoaders } from '@/modules/ads/adUtils';
import { useTracking } from '@/modules/tracking/composables/useTracking';
import {
  getCategoryInfo,
  getCategoryPageNumber,
  getParentRouteKey,
  getCategorySiblings,
  isVideoCategory,
} from '@/modules/category/utils/categoryHelpers';
import { shuffle } from 'lodash';
import SwiperSlide from '@/modules/sliders/core/SwiperSlide';
import WidgetEmojile from '@/modules/emojile/components/WidgetEmojile.vue';
import CategoryPopularArticle from '~/modules/card/templates/category/CategoryPopularArticle';
import ArticleSlider from '~/modules/sliders/components/common/ArticleSlider';
import CarouselArticle from '~/modules/card/templates/common/CarouselArticle';
import OPaginationBase from '~/modules/common/components/ui/OPagination/OPaginationBase';
import { OIcon } from '~/modules/common/components/ui';
import { getCategory } from '~/modules/category/utils/categoryUtils';
import { useDevice } from '~/composables/useDevice';
import { useAuth } from '~/modules/auth/state/useAuth';
import { IS_ONEDIO } from '~/constants/config';
import { useWebSocket } from '~/composables/useWebSocket';
import { useCategory } from '~/modules/category/state/useCategoryState';
import KesfetRedirectButton from '~/modules/kesfet/components/KesfetRedirectButton.vue';

export default defineComponent({
  components: {
    FullPageLayout,
    CategoryArticle,
    CategoryPopularArticle,
    CategorySubBar,
    OPaginationBase,
    CategoryAds,
    AdController,
    ErrorPageLayout,
    OBreadcrumb,
    WidgetEmojile,
    CarouselArticle,
    ArticleSlider,
    SwiperSlide,
    OIcon,
    KesfetRedirectButton,
  },
  setup() {
    const { generateAdConfig } = initializeAdLoaders();
    const serversideAdConfig = generateAdConfig('Category');
    const { $service, $error, $cache, redirect, i18n, $oet } = useContext();
    const { user, isLoggedIn } = useAuth();
    const { Init, Subscribe, Unsubscribe, GetTopicIds } = useWebSocket();
    const { setLiveCounts } = useCategory();
    const categoryData = getCategoryInfo();
    if (!categoryData) {
      redirect(getParentRouteKey());
      return { fetchState: { pending: true } };
    }
    const breadcrumbItems = [
      ...(categoryData.ancestors || []).map((cat) => {
        const categoryInfo = getCategory(cat);
        return {
          href: categoryInfo.href,
          name: categoryInfo.name,
          dropdown: getCategorySiblings(categoryInfo.href)
            .filter((cat) => cat !== categoryInfo.href)
            .map((cat) => {
              const categoryInfo = getCategory(cat);
              return { href: categoryInfo.href, name: categoryInfo.name };
            }),
        };
      }),
      {
        href: categoryData.href,
        name: categoryData.name,
        dropdown: getCategorySiblings(categoryData.href)
          .filter((cat) => cat !== categoryData.href)
          .map((cat) => {
            const categoryInfo = getCategory(cat);
            return { href: categoryInfo.href, name: categoryInfo.name };
          }),
      },
    ];
    const categoryPageNumber = getCategoryPageNumber();
    const isTestCategory = computed(() => categoryData.href === '/test');
    $cache('public, max-age=30');
    const articles = ref({});
    const popular = ref({});
    const popularCarousel = ref([]);
    const pagination = ref({});
    const isSponsored = ssrRef(false);
    const isRecommended = ref(false);
    const { isMobile } = useDevice();
    const title = ref(i18n.t('news-page.popular'));

    // ------- Google Pageview Event Beginning -------
    const { sendPageviewEvent } = useTracking();
    sendPageviewEvent(() => ({
      page: {
        type: 'Kategori Liste',
      },
      content: {
        category1: categoryData.name,
      },
    }));
    // ------- Google Pageview Event End -------

    // old popular articles call
    const getPopularArticles = async () => {
      try {
        const { data: popularFeed } = await $service.category.getCategoryPopularFeed(categoryData._id);
        popular.value = popularFeed;
        isRecommended.value = false;
      } catch (error) {
        $error(error);
      }
    };

    // get articles from new recommended api
    const getRecommendedArticles = async (userId) => {
      try {
        const { ancestors: parents } = categoryData;
        // ana kategoriler kendisini, alt kategoriler ana kategoriyi aliyor
        const category = parents?.length > 0 ? getCategory(parents[0]) : categoryData;
        const { data: popularFeed } = await $service.category.getRecommendedArticles({
          userId,
          categories: category?.name,
          placementId: 1,
        });
        popular.value = shuffle(popularFeed).slice(0, 5);
        isRecommended.value = true;
      } catch (error) {
        await getPopularArticles();
        $error(error);
      }
    };

    if (isRecommended) {
      title.value = i18n.t('news-page.choices');
    }

    // sidebar popular articles
    const sidebarArticles = async () => {
      // gecici olarak sadece onedioda calissin. gelistirme bittiginde bu blog silinecek
      if (!IS_ONEDIO) {
        await getPopularArticles();
        return;
      }

      const anonymUserId = localStorage.getItem('o-oet-ti');
      const userId = isLoggedIn.value ? user.value?._id : JSON.parse(anonymUserId)?.data;
      const { recommendationStatus: status } = await $service.category.getRecommendedApiStatus();
      if (status) {
        await getRecommendedArticles(userId);
        return;
      }
      await getPopularArticles();
    };

    const { fetch, fetchState } = useFetch(async () => {
      try {
        const [{ data, pagination: paginationInfo }] = await Promise.all([
          isVideoCategory(categoryData._id)
            ? $service.category.getVideoCategoryFeed(categoryData._id, categoryPageNumber)
            : $service.category.getCategoryFeed(categoryData._id, categoryPageNumber),
        ]);
        articles.value = data;
        pagination.value = paginationInfo;
        if (articles.value.length === 0 && !pagination.value?.hasNextPage) {
          $error('page not found', 404, false);
        }
        popularCarousel.value = articles.value.slice(0, 5);
        articles.value = articles.value.slice(5, articles.value.length);
        const adConfig = serversideAdConfig(ref(categoryData));
        isSponsored.value = adConfig.value?.decidedMode?.value === 'sponsored';
      } catch (error) {
        $error(error);
      }
    });

    const updateLiveCounts = (currency) => {
      setLiveCounts(currency.value);
    };

    const clientIdChangeCallbackFn = async (clientOldId, clientNewId) => {
      if (clientOldId !== clientNewId) {
        await $oet.sendPushNotificationPlatformInformations({
          userId: user.value?._id,
          notificationTargetValue: clientNewId,
          externalPlatform: 'chatty',
          notificationTargetType: 'chattyId',
        });
      }
    };

    onMounted(async () => {
      sidebarArticles();
      const topicIds = await GetTopicIds();
      if (topicIds[0]) {
        await Init({ forceInit: true, cidChangeCallback: clientIdChangeCallbackFn });
        Subscribe({ topicIds, callback: updateLiveCounts });
      }
    });

    onUnmounted(async () => {
      const topicIds = await GetTopicIds();
      if (topicIds[0]) {
        Unsubscribe({ topicIds });
      }
    });

    useCategoryMeta({ category: categoryData, pagination, breadcrumb: breadcrumbItems, feed: articles });
    return {
      fetch,
      fetchState,
      articles,
      popular,
      popularCarousel,
      categoryPageNumber,
      isMobile,
      pagination,
      categoryData,
      breadcrumbItems,
      isSponsored,
      isTestCategory,
      isRecommended,
      title,
    };
  },
  head: {
    bodyAttrs: {
      class: 'bg-[#f7f8fb] o-ps-global',
    },
  },
});
</script>
